import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, CheckCircle2 } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LOGO_URL } from "../lib/accents";

export default function Register() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [code, setCode] = useState("");
  const [codeInfo, setCodeInfo] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const checkCode = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await api.get(`/access-codes/${encodeURIComponent(code.trim())}/validate`);
      setCodeInfo(data);
    } catch (e) {
      setCodeInfo(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    const name = `${firstName.trim()} ${lastName.trim()}`.trim();
    try {
      const { data } = await api.post("/auth/register", {
        code: code.trim(),
        name,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email,
        password,
      });
      setUser(data);
      toast.success("Welcome to the Lookbook!");
      navigate("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-8">
      <div className="w-full max-w-md fade-up text-center">
        <img src={LOGO_URL} alt="UnCloned®" className="h-12 sm:h-14 w-auto mx-auto" />
        <h2 className="font-display font-black text-3xl text-[#000000] mt-3">Create Your Account</h2>
        <p className="text-gray-500 mt-2 text-sm">Enter the access code you received to unlock the ads lookbook.</p>
        <form onSubmit={submit} className="mt-8 space-y-4 text-left">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Access code</label>
            <div className="relative mt-1.5">
              <KeyRound size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                data-testid="register-code-input"
                required value={code}
                onChange={(e) => { setCode(e.target.value.toUpperCase()); setCodeInfo(null); }}
                onBlur={checkCode}
                className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2.5 text-sm font-mono tracking-wider uppercase focus:outline-none focus:ring-2 focus:ring-[#DF1283] focus:border-transparent"
                placeholder="UNC-XXXXXX"
              />
            </div>
            {codeInfo && (
              <p className="flex items-center gap-1.5 text-xs font-semibold text-[#000000] mt-2" data-testid="register-code-valid">
                <CheckCircle2 size={14} className="text-[#000000]" />
                Code accepted{codeInfo.label ? ` — ${codeInfo.label}` : ""}
              </p>
            )}
            {codeInfo === false && (
              <p className="text-xs font-semibold text-[#DF1283] mt-2" data-testid="register-code-invalid">
                This code is invalid, expired, or fully used.
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">First name</label>
              <input data-testid="register-first-name-input" required value={firstName} onChange={(e) => setFirstName(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
                placeholder="Jane" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Last name</label>
              <input data-testid="register-last-name-input" required value={lastName} onChange={(e) => setLastName(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
                placeholder="Doe" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Email</label>
            <input data-testid="register-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
              placeholder="you@company.com" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Password</label>
              <input data-testid="register-password-input" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
                placeholder="8+ characters" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Confirm</label>
              <input data-testid="register-confirm-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
                placeholder="Repeat" />
            </div>
          </div>
          <button data-testid="register-submit-btn" type="submit" disabled={loading}
            className="w-full bg-[#DF1283] text-white rounded-full px-6 py-3 font-bold text-sm tracking-[0.08em] hover:bg-[#000000] transition-colors duration-200 disabled:opacity-60">
            {loading ? "CREATING ACCOUNT..." : "CREATE ACCOUNT"}
          </button>
        </form>
        <p className="text-sm text-gray-500 mt-6">
          Already a member?{" "}
          <Link to="/login" data-testid="register-login-link" className="font-bold text-[#DF1283] hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
